/**
 * Employee Directory API + TanStack Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import type {
  EmployeeDetail,
  EmployeeRow,
  EmploymentType,
  UpdateEmployeeInput,
} from '../types';

interface ListParams {
  search?: string;
  teamId?: string;
  role?: string;
  employmentType?: EmploymentType[];
  includeAll?: boolean;
  /** When true, include former employees (disabled or past employment_end_date). */
  includeFormer?: boolean;
}

interface ListResponse {
  employees: EmployeeRow[];
  total: number;
}

export const employeeKeys = {
  all: ['teams', 'employees'] as const,
  list: (params?: ListParams) => [...employeeKeys.all, 'list', params ?? {}] as const,
  detail: (id: string) => [...employeeKeys.all, 'detail', id] as const,
};

export const listEmployees = (params?: ListParams) =>
  api.get<ListResponse>('/v4/employees', params as Record<string, unknown>);

export const getEmployee = (id: string) =>
  api.get<EmployeeDetail>(`/v4/employees/${id}`);

export const updateEmployee = (id: string, patch: UpdateEmployeeInput) =>
  api.patch<EmployeeDetail>(`/v4/employees/${id}`, patch);

export const updateEmployeePermissions = (id: string, permissionStrings: string[]) =>
  api.put<{ user: { id: string; permission_strings: string[] } }>(`/v4/users/${id}/permission_strings`, {
    permission_strings: permissionStrings,
  });

export const grantEmployeeRole = (id: string, role: string, reason?: string) =>
  api.post<EmployeeDetail>(`/v4/employees/${id}/roles`, { role, reason });

export const revokeEmployeeRole = (id: string, role: string, reason?: string) => {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  return api.delete<EmployeeDetail>(`/v4/employees/${id}/roles/${encodeURIComponent(role)}${qs}`);
};

export function useEmployees(params?: ListParams) {
  return useQuery({
    queryKey: employeeKeys.list(params),
    queryFn: () => listEmployees(params),
  });
}

export function useEmployee(id: string | null) {
  return useQuery({
    queryKey: employeeKeys.detail(id ?? ''),
    queryFn: () => getEmployee(id!),
    enabled: !!id,
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateEmployeeInput }) =>
      updateEmployee(id, patch),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: employeeKeys.all });
      qc.invalidateQueries({ queryKey: employeeKeys.detail(id) });
      toast.success('Employee updated');
    },
    onError: (err: Error) => toast.error('Failed to update employee', { description: err.message }),
  });
}

export function useUpdateEmployeePermissions() {
  const qc = useQueryClient();
  const { user: currentUser, invalidateSession } = useSession();

  return useMutation({
    mutationFn: ({ id, permissionStrings }: { id: string; permissionStrings: string[]; permissionKey?: string }) =>
      updateEmployeePermissions(id, permissionStrings),
    onMutate: async ({ id, permissionStrings }) => {
      await qc.cancelQueries({ queryKey: employeeKeys.all });

      const previousDetail = qc.getQueryData<EmployeeDetail>(employeeKeys.detail(id));
      const previousLists = qc.getQueriesData<ListResponse>({ queryKey: employeeKeys.all });

      qc.setQueryData<EmployeeDetail>(employeeKeys.detail(id), (old) => (
        old ? { ...old, permission_strings: permissionStrings } : old
      ));
      qc.setQueriesData<ListResponse>({ queryKey: employeeKeys.all }, (old) => {
        if (!old || !Array.isArray(old.employees)) return old;
        return {
          ...old,
          employees: old.employees.map((employee) => (
            employee.id === id ? { ...employee, permission_strings: permissionStrings } : employee
          )),
        };
      });

      return { previousDetail, previousLists };
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: employeeKeys.all });
      qc.invalidateQueries({ queryKey: employeeKeys.detail(id) });
      if (currentUser?._id === id) {
        invalidateSession();
      }
      toast.success('Permissions updated');
    },
    onError: (err: Error, { id }, context) => {
      if (context?.previousDetail) {
        qc.setQueryData(employeeKeys.detail(id), context.previousDetail);
      }
      context?.previousLists.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data);
      });
      toast.error('Failed to update permissions', { description: err.message });
    },
  });
}

export function useGrantEmployeeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role, reason }: { id: string; role: string; reason?: string }) =>
      grantEmployeeRole(id, role, reason),
    onSuccess: (_d, { id, role }) => {
      qc.invalidateQueries({ queryKey: employeeKeys.all });
      qc.invalidateQueries({ queryKey: employeeKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['teams', 'role-definitions'] });
      toast.success(`Role "${role}" granted`);
    },
    onError: (err: Error) => toast.error('Failed to grant role', { description: err.message }),
  });
}

export function useRevokeEmployeeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role, reason }: { id: string; role: string; reason?: string }) =>
      revokeEmployeeRole(id, role, reason),
    onSuccess: (_d, { id, role }) => {
      qc.invalidateQueries({ queryKey: employeeKeys.all });
      qc.invalidateQueries({ queryKey: employeeKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['teams', 'role-definitions'] });
      toast.success(`Role "${role}" revoked`);
    },
    onError: (err: Error) => toast.error('Failed to revoke role', { description: err.message }),
  });
}
